// 每个页面的 title / description / 分享卡片。
//
// 站点是纯 CSR SPA：所有路由共用同一个 index.html，所以在这之前
// 每个页面在搜索结果和分享卡片里显示的都是首页信息。
//
// 这一份只负责「算出应该是什么」，纯函数、可测；
// 真正写进 <head> 的是 useDocumentMeta.js。
// 社交平台的爬虫不执行 JS，拿不到这里算出来的东西——那条路由由
// api/prerender.js 在服务端兜底，两边共用这个文件。

export const SITE_NAME = 'p1ur'
export const SITE_URL = 'https://www.p1ur.com'

export const DEFAULT_TITLE = `${SITE_NAME} | Intimate Wellness & Lingerie`
export const DEFAULT_DESCRIPTION =
  'Shop p1ur for intimate wellness products and lingerie. Thoughtfully selected, discreetly packaged, and shipped fast within the US.'
export const DEFAULT_IMAGE =
  'https://res.cloudinary.com/dmhl3ukf9/image/upload/w_1200,h_630,c_fill,q_auto,f_auto/v1768084615/Gray_and_Black_Minimalist_Fashion_Style_Banner_1_uogn8n.png'

// 搜索结果里大约 160 字符之后就被截断了，与其让 Google 拦腰砍断，不如自己按词切。
export const DESCRIPTION_LIMIT = 160

/**
 * 把商品文案压成一行能进 meta 的纯文本。
 * 商品的 productDetails 是「• 一条\n• 一条」这种项目符号列表，
 * 原样塞进 description 会带着换行和圆点出现在搜索结果里。
 */
export function plainText(value) {
  if (typeof value !== 'string') return ''
  return value
    .replace(/<[^>]*>/g, ' ')      // 万一以后允许富文本
    .replace(/[•·▪●]/g, ' ')       // 项目符号
    .replace(/\s+/g, ' ')          // 换行和连续空格压成一个空格
    .trim()
}

/** 按词切，不把单词劈成两半；切了才加省略号 */
export function truncate(text, limit = DESCRIPTION_LIMIT) {
  const clean = plainText(text)
  if (clean.length <= limit) return clean
  const cut = clean.slice(0, limit)
  const lastSpace = cut.lastIndexOf(' ')
  // 没有空格说明是一整串没有分词的文字（比如中文），那就直接切
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/** 页面标题统一带站名后缀；首页不重复带 */
export function pageTitle(title) {
  const clean = plainText(title)
  if (!clean) return DEFAULT_TITLE
  if (clean === SITE_NAME || clean.endsWith(`| ${SITE_NAME}`)) return clean
  return `${clean} | ${SITE_NAME}`
}

/**
 * canonical 只认路径，不带 query。
 * ?orderNo=…、?utm_source=… 这类参数如果进了 canonical，
 * 每一次分享都会变成一个新的「页面」，权重被摊薄。
 */
export function canonicalUrl(pathname = '/') {
  const path = String(pathname).split('?')[0].split('#')[0]
  if (!path || path === '/') return `${SITE_URL}/`
  return SITE_URL + (path.startsWith('/') ? path : `/${path}`).replace(/\/+$/, '')
}

/**
 * 分享卡片要 1200×630。商品图存的是 w_1200,h_1200,c_limit（正方形），
 * 直接拿去当 og:image 会被各平台自己裁，裁到哪儿全看运气。
 * 这里把 Cloudinary 的变换段换成 630 高的填充裁剪；不是 Cloudinary 的地址就原样返回。
 */
export function ogImage(url) {
  if (typeof url !== 'string' || !url) return DEFAULT_IMAGE
  const marker = '/image/upload/'
  const at = url.indexOf(marker)
  if (!url.includes('res.cloudinary.com') || at === -1) return url

  const head = url.slice(0, at + marker.length)
  let tail = url.slice(at + marker.length)
  // 已有的变换段（第一段里带 w_/h_/c_ 这类参数）去掉，换成我们的
  const firstSlash = tail.indexOf('/')
  const firstSegment = firstSlash === -1 ? tail : tail.slice(0, firstSlash)
  if (/(^|,)(w|h|c|q|f|e|dpr)_/.test(firstSegment)) tail = tail.slice(firstSlash + 1)
  return `${head}w_1200,h_630,c_fill,q_auto,f_auto/${tail}`
}

/**
 * 一个页面最终要写进 <head> 的全部东西。
 * @param {object} input
 * @param {string} [input.title] 不带站名后缀的页面标题
 * @param {string} [input.description]
 * @param {string} [input.image] 分享图（会被 ogImage 归一化）
 * @param {string} [input.path] 当前路径，用来算 canonical
 * @param {'website'|'product'|'article'} [input.type]
 * @param {boolean} [input.noindex] 不该被收录的页面（购物车、结账、订单、个人中心）
 */
export function buildMeta({ title, description, image, path = '/', type = 'website', noindex = false } = {}) {
  return {
    title: pageTitle(title),
    description: truncate(description || DEFAULT_DESCRIPTION),
    canonical: canonicalUrl(path),
    image: ogImage(image),
    type,
    // noindex 的页面连 canonical 都不该给——那等于在请求收录
    robots: noindex ? 'noindex, nofollow' : 'index, follow',
    noindex,
  }
}

/**
 * 商品详情页要用的那几项。返回的是 buildMeta 的入参，不是最终结果——
 * path 由 useDocumentMeta 从当前路由填，页面不用自己传。
 */
export function productSeo(product) {
  if (!product) return {}
  const name = plainText(product.name?.en || product.name)
  const detail = plainText(product.productDetails)
  return {
    title: name,
    // 商品没有独立的 description 字段，用 productDetails 的要点凑；
    // 连它也没有时退回一句带商品名的话，总好过所有商品共用同一段默认文案。
    description: detail || `${name} — available at ${SITE_NAME}. Discreet packaging, fast US shipping.`,
    image: (product.images && product.images[0]) || product.image,
    type: 'product',
  }
}

/** 分类页 */
export function categorySeo(category) {
  const name = plainText(category)
  if (!name) return {}
  return {
    title: name,
    description: `Browse ${name.toLowerCase()} at ${SITE_NAME}. Thoughtfully selected, discreetly packaged, shipped fast within the US.`,
  }
}

/**
 * 路由 → 这个页面的 meta 入参。**浏览器和爬虫都走这一个函数**，
 * 否则两边会各写一份，慢慢漂成两个说法。
 *
 * @param {string} pathname 只要路径，不要 query
 * @param {{product?: object}} [data] 需要异步数据才能算出来的部分
 */
export function seoForRoute(pathname = '/', data = {}) {
  const path = String(pathname).split('?')[0].split('#')[0].replace(/\/+$/, '') || '/'

  if (path.startsWith('/product/')) {
    // 商品确实不存在（不是「还在加载」）：别让一个死链接带着默认文案进索引
    if (data.notFound) return { title: 'Product not found', noindex: true }
    return productSeo(data.product)
  }
  if (path === '/vibrators') return categorySeo('Vibrators')
  if (path === '/lingerie') return categorySeo('Sexy Lingerie')

  switch (path) {
    case '/':
      return {}
    // 同一个页面挂在两个路径上。canonical 统一指向 /all-products（页头链的是它），
    // 否则搜索引擎会当成两个内容重复的页面各收一遍，互相摊薄权重。
    case '/products':
    case '/all-products':
      return { title: 'All Products', path: '/all-products' }
    case '/about':
      return {
        title: 'About',
        description: 'Why p1ur exists, how we pick what we sell, and how we ship it — discreetly.',
      }
    // 下面这些不该进搜索结果：要么是私人内容，要么对没上下文的人毫无意义
    case '/cart':
      return { title: 'Cart', noindex: true }
    case '/checkout':
      return { title: 'Checkout', noindex: true }
    case '/order-success':
      return { title: 'Order Confirmed', noindex: true }
    case '/profile':
      return { title: 'Account', noindex: true }
    case '/oauth/callback':
      return { title: 'Signing in', noindex: true }
    default:
      if (path.startsWith('/order/')) return { title: 'Order', noindex: true }
      // 没登记的路径一律不收录：与其让一个 404 或将来新加的页面
      // 带着首页文案进索引，不如先挡住。
      return { noindex: true }
  }
}

/**
 * 这个页面在这个环境里该不该被收录。
 *
 * dev / preview 环境一律不许被收录：内容和生产站几乎一样，被收进去就是
 * 拿自己的站和自己抢同一批关键词，而且用户会点进一个随时会被推翻的环境。
 */
export function shouldIndex(appEnv, noindex) {
  return appEnv === 'production' && !noindex
}

/**
 * 路由 → 最终要写进 <head> 的完整 meta。浏览器端和爬虫端都调这个，
 * 别在各自那边再拼一次「input.path 还是当前路径」——重复一次就会漂一次。
 */
export function metaForRoute(pathname = '/', data = {}) {
  const input = seoForRoute(pathname, data)
  return buildMeta({ ...input, path: input.path ?? pathname })
}

